import { formatCurrency, formatString, MONTHS } from "./functions.js";
import { DashboardResultLine } from "./server-types/DashboardResultLine.js";

import { app } from "./session.js";

class SalesReportMtd {
    private currentYear: number;
    private plans: DashboardResultLine[] = [];
    currentMonth: number;

    constructor() {
        const currentDate = new Date();
        this.currentYear = currentDate.getFullYear();
        this.currentMonth = currentDate.getMonth();
        this.init();
    }

    private async init() {
        try {
            await this.render();
            await this.loadPlans();
        } catch (error) {
            console.error('Failed to initialize Sales Report Manager:', error);
            this.showError('Failed to load data. Please refresh the page.');
        }
    }

    private async loadPlans() {
        try {
            const list = document.getElementById('plans-list');
            if (list)
                list.innerHTML = "Loading...";

            // Load plans for the selected year (using month 0 as default)
            this.plans = await app.API.dashboard(this.currentYear, this.currentMonth);
            this.renderPlansList();
        } catch (error) {
            console.error('Failed to load plans:', error);
            this.showError('Failed to load plans for the selected year.');
        }
    }

    private async render() {
        const content = document.getElementById('content');
        if (!content) {
            throw new Error('Content element not found');
        }

        content.innerHTML = `
            <div class="sales-report-manager">
                <h2>Update Month-To-Date</h2>
                
                <div class="controls-section">
                    <div class="date-selector">
                        <label for="year-select">Select Year:</label>
                        <select id="year-select" class="form-control">
                            ${this.generateYearOptions()}
                        </select>

                        <label for="month-select">Select Month:</label>
                        <select id="month-select" class="form-control">
                            ${this.generateMonthOptions()}
                        </select>
                    </div>
               </div>

                <div id="plans-list" class="plans-section">
                    <div class="loading">Loading plans...</div>
                </div>

                <div id="edit-mtd-modal" class="modal" style="display: none;">
                    <div class="modal-content">
                        <h3>Edit Month-To-Date</h3>

                        <h4 id="edit-mtd-title"></h4>
                        <form id="edit-mtd-form">
                            <input type="hidden" id="edit-mtd-id">
                            <input type="hidden" id="edit-mtd-year">
                            <input type="hidden" id="edit-mtd-month">
                           
                            <div class="form-group">
                                <label for="edit-mtd-value">Month-To-Date:</label>
                                <input type="number" id="edit-mtd-value" class="form-control" required>
                            </div>
                            
                            <div class="form-actions">
                                <button type="submit" class="btn btn-success">Update Month-To-Date</button>
                                <button type="button" id="cancel-edit" class="btn btn-secondary">Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        `;

        this.attachEventListeners();
    }
    generateMonthOptions() {
        let options = '';
        for (let month = 0; month < 12; month++) {
            const selected = month === this.currentMonth ? 'selected' : '';
            options += `<option value="${month}" ${selected}>${MONTHS[month]}</option>`;
        }
        return options;
    }

    private generateYearOptions(): string {
        const currentYear = new Date().getFullYear();
        const startYear = 2025;
        const endYear = Math.max(currentYear, this.currentYear);

        let options = '';
        for (let year = startYear; year <= endYear; year++) {
            const selected = year === this.currentYear ? 'selected' : '';
            options += `<option value="${year}" ${selected}>${year}</option>`;
        }
        return options;
    }

    private attachEventListeners() {
        // Year selector change
        const yearSelect = document.getElementById('year-select') as HTMLSelectElement;
        yearSelect?.addEventListener('change', (e) => {
            this.currentYear = parseInt((e.target as HTMLSelectElement).value);
            this.loadPlans();
        });

        const monthSelect = document.getElementById('month-select') as HTMLSelectElement;
        monthSelect?.addEventListener('change', (e) => {
            this.currentMonth = parseInt((e.target as HTMLSelectElement).value);
            this.loadPlans();
        });

        // Edit plan form
        const editForm = document.getElementById('edit-mtd-form');
        editForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleEditMtd();
        });

        const editCancel = document.getElementById('cancel-edit');
        editCancel?.addEventListener('click', (e) => {
            e.preventDefault();
            this.hideEditModal();
        });
    }

    private renderPlansList() {
        const plansList = document.getElementById('plans-list');
        if (!plansList) return;

        if (this.plans.length === 0) {
            plansList.innerHTML = `
                <div class="no-plans">
                    <p>No plans found for ${this.currentYear}.</p>
                </div>
            `;
            return;
        }

        // Group plans by user
        const plansByUser = this.groupPlansByUser();

        let html = '<table>';

        html += `<thead>
            <tr>
                <th>Name</th>
                <th>Plan Name</th>
                <th>Month To Date</th>
                <th>Edit</th>
            </tr>
        </thead>
        <tbody>`;

        for (const [_, userPlans] of Object.entries(plansByUser)) {
            for (const plan of userPlans) {
                if (plan.goal === 0) continue;
                html += `<tr>
                    <td>${formatString(plan.userName)}</td>
                    <td>${formatString(plan.planName)}</td>
                    <td>${formatCurrency(plan.monthToDate)}</td>
                    <td>
                    <div class="plan-actions">
                        <button class="edit-mtd-btn" data-plan-id="${plan.id}">Edit</button>
                    </div>
                    </td>
                </tr>`;
            }
        }

        html += `</tbody>
        </table>`;
        plansList.innerHTML = html;

        // Attach plan-specific event listeners
        this.attachPlanEventListeners();
    }

    private groupPlansByUser(): Record<string, DashboardResultLine[]> {
        return this.plans.reduce((acc, plan) => {
            if (!acc[plan.userId]) {
                acc[plan.userId] = [];
            }
            acc[plan.userId].push(plan);
            return acc;
        }, {} as Record<string, DashboardResultLine[]>);
    }

    private attachPlanEventListeners() {
        // Edit buttons
        document.querySelectorAll('.edit-mtd-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const planId = (e.target as HTMLElement).getAttribute('data-plan-id');
                const plan = this.plans.find(p => p.id === planId);
                if (plan) {
                    this.editPlan(plan);
                }
            });
        });
    }

    private showEditModal() {
        const modal = document.getElementById('edit-mtd-modal');
        if (modal) {
            modal.style.display = '';
        }
    }

    private hideEditModal() {
        const modal = document.getElementById('edit-mtd-modal');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    private async editPlan(plan: DashboardResultLine) {
        try {
            // Populate the edit form
            (document.getElementById('edit-mtd-id') as HTMLInputElement).value = plan.id ?? "";
            (document.getElementById('edit-mtd-year') as HTMLInputElement).value = plan.year.toString();
            (document.getElementById('edit-mtd-month') as HTMLInputElement).value = plan.month.toString();
            (document.getElementById('edit-mtd-title') as HTMLElement).innerText = formatString(`${plan.userName} ${plan.planName ? `(${plan.planName})` : ``}`);

            this.showEditModal();
            const input = document.getElementById(`edit-mtd-value`) as HTMLInputElement;
            if (input) {
                input.value = plan.monthToDate.toString();
                input.focus();
                input.select();
            }

        } catch (error) {
            console.error('Error loading plan for edit:', error);
            this.showError('Failed to load plan data for editing.');
        }
    }

    private async handleEditMtd() {
        const id = (document.getElementById("edit-mtd-id") as HTMLInputElement).value;
        const month = parseInt((document.getElementById("edit-mtd-month") as HTMLInputElement).value);
        const mtd = parseInt((document.getElementById("edit-mtd-value") as HTMLInputElement).value);

        try {
            await app.API.updatePlanMtd(id, month, mtd);
            this.hideEditModal();
            await this.loadPlans(); // Refresh the plans list
        } catch (error) {
            console.error('Error updating plan:', error);
            this.showError('An error occurred while updating the plan.');
        }
    }

    private showError(message: string) {
        // Simple error display - you might want to replace with your preferred notification system
        alert(`Error: ${message}`);
    }
}

// Initialize the manager when the page loads
export function initializeSalesReportMtd() {
    new SalesReportMtd();
}
